import { Ionicons } from '@expo/vector-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  AppState,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { CollapsibleHeader } from '@/components/home/CollapsibleHeader';
import { DaySummaryCard } from '@/components/home/DaySummaryCard';
import { FamilyAvatarsRow } from '@/components/home/FamilyAvatarsRow';
import { UpcomingCard } from '@/components/home/UpcomingCard';
import { SuggestionBlankState } from '@/components/home/SuggestionBlankState';
import { MemoriesSection } from '@/components/home/MemoriesSection';
import { TodayRouteCard } from '@/components/home/TodayRouteCard';
import { CalendarEventModal } from '@/components/calendar/event-modal';
import { useFamilyMembersStore, useCalendarMemberMappingStore } from '@/hooks/use-scoped-managers';
import { Colors, ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { HERO_HEIGHT_MAX, HERO_HEIGHT_MIN } from '@/constants/layout';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';
import { useUpcomingEvent } from '@/hooks/use-upcoming-event';
import { useCalendarMemberMapping } from '@/hooks/use-calendar-member-mapping';
import { useHomeScreenHandlers } from '@/hooks/use-home-screen-handlers';
import { useHomeEventModal } from '@/hooks/use-home-event-modal';
import { useMeDisplayName } from '@/hooks/use-me-display-name';
import { useCollapsibleHeader } from '@/hooks/use-collapsible-header';
import { useCoverImage } from '@/hooks/use-cover-image';
import { HomeScreenManager } from '@/lib/managers/HomeScreenManager';
import { locationService } from '@/lib/services/LocationService';
import { useSupabaseAuth } from '@/lib/supabase';
import type { HomeSummary, SummaryPreferences } from '@/types/home-summary';

export default function HomeScreen() {
  const colorScheme = useColorScheme();
  const { session } = useSupabaseAuth();
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [, setIsSyncingCalendars] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<SummaryPreferences | null>(null);
  const [isPreferencesLoading, setIsPreferencesLoading] = useState(false);
  const [isDeveloperGenerating, setIsDeveloperGenerating] = useState(false);
  const [isRefreshingStale, setIsRefreshingStale] = useState(false);
  const [foregroundKey, setForegroundKey] = useState(0);
  const appStateRef = useRef(AppState.currentState);

  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();
  const tabBarHeight = insets.bottom || 64;

  // Cover image
  const { coverImageUri, pickCoverImage, removeCoverImage, hasCoverImage } = useCoverImage();

  // Collapsible header animation
  const { scrollHandler, headerTranslateStyle, dateAnimatedStyle, editButtonAnimatedStyle } = useCollapsibleHeader({
    maxHeight: HERO_HEIGHT_MAX,
    minHeight: HERO_HEIGHT_MIN,
  });

  // Manager instance
  const homeManager = useMemo(() => HomeScreenManager.getInstance(), []);

  // Family members store (scoped)
  const familyMembersStore = useFamilyMembersStore();
  const calendarMappingStore = useCalendarMemberMappingStore();

  // Get upcoming events (up to 3) plus today's completed/all-day events
  const {
    events: upcomingEvents,
    todaysAllDayEvents,
    todaysCompletedEvents,
    isLoading: isLoadingEvents,
    refresh: refreshEvents,
  } = useUpcomingEvent();
  const { mappings: calendarMappings } = useCalendarMemberMapping();

  const eventModal = useHomeEventModal({ refreshEvents, familyMembersStore });

  // Recalculate today when app returns to foreground (handles overnight staleness)
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (appStateRef.current.match(/inactive|background/) && nextState === 'active') {
        setForegroundKey(k => k + 1);
      }
      appStateRef.current = nextState;
    });
    return () => subscription.remove();
  }, []);

  // Get today's date in ISO format (YYYY-MM-DD) using user's timezone
  const today = useMemo(() => {
    const timezone = preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    }).format(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.timezone, foregroundKey]);

  // Handlers hook
  const handlers = useHomeScreenHandlers({
    session,
    preferences,
    setSummary,
    setIsDeveloperGenerating,
  });

  // Fetch preferences on mount
  const fetchPreferences = useCallback(async () => {
    if (!session) return null;

    setIsPreferencesLoading(true);
    try {
      const prefs = await homeManager.fetchPreferences();
      setPreferences(prefs);
      return prefs;
    } catch (err) {
      console.error('[HomeScreen] Failed to load summary preferences:', err);
      setPreferences(null);
      setSummaryError((err as Error).message);
      return null;
    } finally {
      setIsPreferencesLoading(false);
    }
  }, [session, homeManager]);

  // Load summary
  const loadSummary = useCallback(
    async (options: { forceRegenerate?: boolean } = {}) => {
      if (!session?.user?.id || !preferences) return;

      setSummaryError(null);
      setIsSummaryLoading(true);

      try {
        // Fetch location for weather context (best-effort, non-blocking)
        const location = await locationService.getLocation().catch(() => null);

        const result = await homeManager.loadSummary({
          userId: session.user.id,
          preferences,
          today,
          forceRegenerate: options.forceRegenerate,
          onSyncingChange: setIsSyncingCalendars,
          onRefreshStarted: () => setIsRefreshingStale(true),
          onSummaryUpdated: (fresh) => {
            setSummary(fresh);
            setIsRefreshingStale(false);
          },
          familyMembersStore,
          calendarMappingStore,
          ...(location && { latitude: location.latitude, longitude: location.longitude }),
        });

        setSummary(result);
      } catch (err) {
        console.error('[HomeScreen] Failed to load/generate summary:', err);
        setSummaryError((err as Error).message);
        setIsRefreshingStale(false);
      } finally {
        setIsSyncingCalendars(false);
        setIsSummaryLoading(false);
      }
    },
    [session?.user?.id, preferences, homeManager, today, familyMembersStore, calendarMappingStore]
  );

  // Pull-to-refresh: refresh events and reload the summary. Intentionally does
  // NOT force-regenerate the AI summary, to avoid burning the user's daily AI quota.
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const handlePullRefresh = useCallback(async () => {
    setIsPullRefreshing(true);
    try {
      await Promise.all([refreshEvents(), loadSummary()]);
    } finally {
      setIsPullRefreshing(false);
    }
  }, [refreshEvents, loadSummary]);

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Load summary when preferences are available
  useEffect(() => {
    if (!preferences) {
      setSummary(null);
      return;
    }

    loadSummary();
  }, [preferences, loadSummary]);

  // Cover image edit handler
  const handleEditCoverPress = useCallback(() => {
    const options: string[] = ['Choose Photo'];
    if (hasCoverImage) {
      options.push('Default Cover');
    }
    options.push('Cancel');

    Alert.alert('Cover Image', undefined,
      options.map((label) => {
        if (label === 'Choose Photo') {
          return { text: label, onPress: pickCoverImage };
        }
        if (label === 'Default Cover') {
          return { text: label, style: 'destructive' as const, onPress: removeCoverImage };
        }
        return { text: label, style: 'cancel' as const };
      })
    );
  }, [hasCoverImage, pickCoverImage, removeCoverImage]);

  // Greeting uses the first-name form (or empty when nothing's available, so
  // the card can render "Hello!" instead of "Hello, Me!").
  const { greetingName, isLoading: isNameLoading } = useMeDisplayName();
  const userNameData = useMemo(
    () => ({ displayName: greetingName, isLoading: isNameLoading }),
    [greetingName, isNameLoading],
  );

  // Format current date for header. Uses the device locale (undefined) and the
  // same preference-driven timezone as the `today` key above, so the header
  // never disagrees with the summary date. foregroundKey re-runs this after
  // the app returns from background (overnight staleness).
  const currentDate = useMemo(() => {
    const timezone =
      preferences?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: timezone,
    }).format(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences?.timezone, foregroundKey]);

  return (
    <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: palette.background }]}>
      {/* Sticky header - positioned absolutely, scrolls via translateY */}
      <CollapsibleHeader
        imageSource={colorScheme === 'dark' ? require('@/assets/images/fim-home-dark.png') : require('@/assets/images/fim-home2.png')}
        currentDate={currentDate}
        headerTranslateStyle={headerTranslateStyle}
        dateAnimatedStyle={dateAnimatedStyle}
        palette={palette}
        coverImageUri={coverImageUri}
        onEditCoverPress={handleEditCoverPress}
        editButtonAnimatedStyle={editButtonAnimatedStyle}
        topInset={insets.top}
      />

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: Math.max(tabBarHeight + 16, 80),
        }}
        refreshControl={
          <RefreshControl
            refreshing={isPullRefreshing}
            onRefresh={handlePullRefresh}
            tintColor={palette.primary}
            progressViewOffset={HERO_HEIGHT_MAX}
          />
        }
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        {/* Spacer for header */}
        <View style={{ height: HERO_HEIGHT_MAX }} />

        <View style={styles.contentWrapper}>
          <View style={[styles.contentBackground, { backgroundColor: palette.background }]} />
          {/* Today's Route - compact tracker, absent when no active plan */}
          <TodayRouteCard />

          {/* Day Summary Card */}
          <View style={styles.section}>
            <DaySummaryCard
              summary={summary}
              isLoading={isSummaryLoading || isPreferencesLoading}
              isRefreshing={isRefreshingStale}
              onActionPress={handlers.handleSummaryAction}
              onReport={handlers.handleReportSummary}
              userName={userNameData}
            />
            {summaryError && !isSummaryLoading && (
              <View style={styles.errorContainer}>
                <Text style={[styles.errorText, { color: palette.textMuted }]}>
                  {summaryError}
                </Text>
                <Pressable onPress={() => loadSummary({ forceRegenerate: true })}>
                  <Text style={[styles.retryText, { color: palette.primary }]}>Try Again</Text>
                </Pressable>
              </View>
            )}
          </View>

          <View style={[styles.divider, { backgroundColor: palette.border }]} />

          {/* Family avatars - tap-through to the Family screen */}
          <FamilyAvatarsRow />

          {/* Earlier Today */}
          {(todaysCompletedEvents.length > 0 || todaysAllDayEvents.length > 0) && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
                  Earlier Today
                </Text>
              </View>
              <UpcomingCard
                events={[]}
                todaysAllDayEvents={todaysAllDayEvents}
                todaysCompletedEvents={todaysCompletedEvents}
                isLoading={false}
                onEventPress={eventModal.handleUpcomingEventPress}
                familyMembers={eventModal.familyMembers}
                calendarMappings={calendarMappings}
                deviceCalendars={eventModal.writableCalendars}
              />
            </View>
          )}

          {/* Coming Up */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: palette.primary, fontFamily: getBodyFont() }]}>
                Coming up
              </Text>
            </View>
            <UpcomingCard
              events={upcomingEvents}
              todaysAllDayEvents={[]}
              todaysCompletedEvents={[]}
              isLoading={isLoadingEvents}
              onEventPress={eventModal.handleUpcomingEventPress}
              familyMembers={eventModal.familyMembers}
              calendarMappings={calendarMappings}
              deviceCalendars={eventModal.writableCalendars}
            />
          </View>

          {/* Suggestion blank state - nudges user to try FAB */}
          <SuggestionBlankState />

          {/* Memories Section */}
          <MemoriesSection memories={[]} />
        </View>
      </Animated.ScrollView>

      {/* View/Edit Event Modal - for viewing and editing upcoming events */}
      <CalendarEventModal
        visible={eventModal.showViewEventModal}
        mode="edit"
        initialEvent={eventModal.selectedEvent}
        writableCalendars={eventModal.writableCalendars}
        allCalendars={eventModal.allCalendars}
        defaultCalendarId={eventModal.selectedEvent?.deviceCalendarId ?? null}
        familyMembers={eventModal.familyMembers}
        calendarMappings={calendarMappings}
        onSave={eventModal.handleViewEventModalSave}
        onDelete={eventModal.handleViewEventModalDelete}
        onDismiss={eventModal.handleViewEventModalDismiss}
        onInstantSave={refreshEvents}
        isSubmitting={eventModal.isSubmittingEvent}
      />

      {/* Developer FAB - only visible in development mode */}
      {__DEV__ && (
        <TouchableOpacity
          style={[
            styles.developerFab,
            {
              backgroundColor: ModernPalette.warning,
              top: 100,
              right: 20,
            },
          ]}
          onPress={handlers.handleDeveloperTrigger}
          disabled={isDeveloperGenerating}
          activeOpacity={0.8}
        >
          {isDeveloperGenerating ? (
            <ActivityIndicator color={ModernPalette.textInverse} size="small" />
          ) : (
            <Ionicons name="sparkles" size={20} color={ModernPalette.textInverse} />
          )}
        </TouchableOpacity>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentWrapper: {
    paddingHorizontal: Spacing.xl,
    paddingTop: Spacing.sm,
    gap: Spacing.xxl,
  },
  contentBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    opacity: 0.9,
  },
  section: {
    gap: Spacing.sm,
  },
  divider: {
    height: 1,
    marginHorizontal: Spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.xs,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: Spacing.lg,
    paddingHorizontal: Spacing.xl,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: Spacing.sm,
  },
  retryText: {
    fontSize: 14,
    fontWeight: '600',
  },
  developerFab: {
    position: 'absolute',
    right: Spacing.xl,
    width: 48,
    height: 48,
    borderRadius: BorderRadius.xxl,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 999,
  },
});
